import type { ReactNode } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import useLocalization from 'hooks/useLocalization';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import { ThemeMode } from 'config';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';
import { markSystemNotificationRead } from 'store/slices/system-notifications';
import { markUpdateRead } from 'api/updates';

// assets
import { IconInfoCircle } from '@tabler/icons-react';
import type { SystemNotificationItem } from 'store/slices/system-notifications';
import SystemNotificationLeadingIcon from './SystemNotificationLeadingIcon';

function ListItemWrapper({
  children,
  unread,
  onClick,
}: {
  children: ReactNode;
  unread?: boolean;
  onClick?: () => void;
}) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: unread ? `inset 3px 0 0 ${theme.palette.primary.main}` : 'none',
        cursor: onClick ? 'pointer' : 'default',
        bgcolor:
          unread && theme.palette.mode === ThemeMode.DARK
            ? alpha(theme.palette.primary.main, 0.08)
            : unread
              ? alpha(theme.palette.primary.main, 0.06)
              : 'transparent',
        '&:last-of-type': { borderBottom: 'none' },
        '&:hover': {
          bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.900' : alpha(theme.palette.grey[200], 0.3),
        },
      }}
    >
      {children}
    </Box>
  );
}

function SingleRow({ item, onNavigated }: { item: SystemNotificationItem; onNavigated?: () => void }) {
  const theme = useTheme();
  const { currentLanguage } = useLocalization();
  const dateLocale = currentLanguage === 'en' ? enUS : he;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bg = item.backgroundColor ?? theme.palette.info.main;
  const fg = item.textColor ?? theme.palette.getContrastText(theme.palette.info.main);
  const legacyColoredIcon = !item.visual && (item.backgroundColor || item.textColor);

  const unread = !item.read;

  // שורה עם link היא לחיצה: ניווט ליעד + סימון ההתראה כנקראה (מקומית + בשרת)
  const handleClick = item.link
    ? () => {
        dispatch(markSystemNotificationRead(item.id));
        if (item.fromServer && item.id.startsWith('srv-')) {
          void markUpdateRead(item.id.slice(4));
        }
        navigate(item.link!);
        onNavigated?.();
      }
    : undefined;

  return (
    <ListItemWrapper unread={unread} onClick={handleClick}>
      <ListItem alignItems="flex-start" disablePadding>
        <ListItemAvatar sx={{ minWidth: 48, mt: 0.25 }}>
          {item.visual ? (
            <SystemNotificationLeadingIcon visual={item.visual} />
          ) : legacyColoredIcon ? (
            <Avatar
              sx={{
                bgcolor: bg,
                color: fg,
                width: 40,
                height: 40,
              }}
            >
              <IconInfoCircle stroke={1.5} size={20} />
            </Avatar>
          ) : (
            <SystemNotificationLeadingIcon />
          )}
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ lineHeight: 1.43, whiteSpace: 'pre-line' }}>
              {item.message}
            </Typography>
          }
          secondary={
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {formatDistanceToNow(item.createdAt, { addSuffix: true, locale: dateLocale })}
            </Typography>
          }
        />
      </ListItem>
    </ListItemWrapper>
  );
}

interface NotificationListProps {
  emptyLabel: string;
  /** נקרא אחרי ניווט מתוך שורה לחיצה — לסגירת הפאנל */
  onNavigated?: () => void;
}

export default function NotificationList({ emptyLabel, onNavigated }: NotificationListProps) {
  const items = useSelector((state) => state.systemNotifications.items);

  if (items.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: '100%', maxWidth: { xs: 300, md: 360 }, py: 0 }}>
      {items.map((item) => (
        <SingleRow key={item.id} item={item} onNavigated={onNavigated} />
      ))}
    </List>
  );
}
